/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router";
import { PrimaryButton } from "../../components";
import { useBeevisionAuditLogs } from "../../store/useBeevisionAuditLogs";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { BeevisionAuditLogsTable } from "./BeevisionAuditLogsTable";
import { BeevisionAuditLogsFilters } from "./BeevisionAuditLogsFilters";

function BeevisionAuditLogsList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logs, getLogs, total, limit, downloadCsv } = useBeevisionAuditLogs();
  const [isDownloading, setIsDownloading] = useState(false);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  );
  const [dayFilter, setDayFilter] = useState<number | undefined>(undefined);
  const [monthFilter, setMonthFilter] = useState<number | undefined>(undefined);
  const [yearFilter, setYearFilter] = useState<number | undefined>(undefined);
  const [locationsFilter, setLocationsFilter] = useState<string[]>([]);
  const [materialsFilter, setMaterialsFilter] = useState<string[]>([]);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const filtersLoadedFromUrl = useRef(false);

  const handleApplyFilters = (filters: {
    status?: string;
    day?: number;
    month?: number;
    year?: number;
    locations?: string[];
    materials?: string[];
    search?: string;
  }) => {
    setStatusFilter(filters.status);
    setDayFilter(filters.day);
    setMonthFilter(filters.month);
    setYearFilter(filters.year);
    setLocationsFilter(filters.locations ?? []);
    setMaterialsFilter(filters.materials ?? []);
    setSearchFilter(filters.search ?? "");
    setPage(1); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setStatusFilter(undefined);
    setDayFilter(undefined);
    setMonthFilter(undefined);
    setYearFilter(undefined);
    setLocationsFilter([]);
    setMaterialsFilter([]);
    setSearchFilter("");
    setPage(1);
    navigate(location.pathname, { replace: true });
  };

  useEffect(() => {
    if (filtersLoadedFromUrl.current) return;

    const searchParams = new URLSearchParams(location.search);
    const status = searchParams.get("status") || undefined;
    const day = searchParams.get("day")
      ? Number(searchParams.get("day"))
      : undefined;
    const month = searchParams.get("month")
      ? Number(searchParams.get("month"))
      : undefined;
    const year = searchParams.get("year")
      ? Number(searchParams.get("year"))
      : undefined;
    const locations =
      searchParams.get("locations")?.split(",").filter(Boolean) || [];
    const materials =
      searchParams.get("materials")?.split(",").filter(Boolean) || [];
    const search = searchParams.get("search") || "";

    if (status) setStatusFilter(status);
    if (day) setDayFilter(day);
    if (month) setMonthFilter(month);
    if (year) setYearFilter(year);
    if (locations.length > 0) setLocationsFilter(locations);
    if (materials.length > 0) setMaterialsFilter(materials);
    if (search) setSearchFilter(search);

    filtersLoadedFromUrl.current = true;

    requestAnimationFrame(() => {
      setFiltersInitialized(true);
    });
  }, []);

  useEffect(() => {
    if (!filtersInitialized) return;

    const params = new URLSearchParams();

    if (statusFilter) params.set("status", statusFilter);
    if (dayFilter) params.set("day", String(dayFilter));
    if (monthFilter) params.set("month", String(monthFilter));
    if (yearFilter) params.set("year", String(yearFilter));
    if (locationsFilter.length > 0)
      params.set("locations", locationsFilter.join(","));
    if (materialsFilter.length > 0)
      params.set("materials", materialsFilter.join(","));
    if (searchFilter) params.set("search", searchFilter);

    const newSearch = params.toString();
    const currentSearch = location.search.replace(/^\?/, "");
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;

    if (currentSearch !== newSearch) {
      navigate(newUrl, { replace: true });
    }
  }, [
    statusFilter,
    dayFilter,
    monthFilter,
    yearFilter,
    locationsFilter,
    materialsFilter,
    searchFilter,
    filtersInitialized,
  ]);

  useEffect(() => {
    if (!filtersInitialized) return;

    getLogs(
      page,
      limit,
      statusFilter,
      dayFilter,
      monthFilter,
      yearFilter,
      locationsFilter,
      materialsFilter,
      searchFilter
    );
  }, [
    page,
    limit,
    statusFilter,
    dayFilter,
    monthFilter,
    yearFilter,
    locationsFilter,
    materialsFilter,
    searchFilter,
    filtersInitialized,
    getLogs,
  ]);

  return (
    <div>
      <div className="flex w-full justify-between items-center mb-11">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Dropbot Audit Logs
        </h1>
        <PrimaryButton
          label={isDownloading ? "Downloading..." : "Download CSV"}
          disabled={isDownloading}
          onClick={async () => {
            setIsDownloading(true);
            try {
              await downloadCsv(
                statusFilter,
                dayFilter,
                monthFilter,
                yearFilter,
                locationsFilter,
                materialsFilter,
                searchFilter
              );
            } finally {
              setIsDownloading(false);
            }
          }}
        />
      </div>
      <BeevisionAuditLogsFilters
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        initialFilters={{
          status: statusFilter,
          day: dayFilter,
          month: monthFilter,
          year: yearFilter,
          locations: locationsFilter,
          materials: materialsFilter,
          search: searchFilter,
        }}
      />
      <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none">
        {!logs?.length ? (
          <p className="text-center font-satoshi text-base text-dark">
            There are no Dropbot audit logs yet.
          </p>
        ) : (
          <BeevisionAuditLogsTable />
        )}
      </div>
      {total > logs.length ? (
        <div className="flex flex-row w-full items-center justify-end mt-3">
          <PrimaryButton
            onClick={() => {
              setPage(page + 1);
            }}
            label="Load more"
            className="h-10"
          />
        </div>
      ) : null}
    </div>
  );
}

export default withProtectedRoute(BeevisionAuditLogsList);
